import { useCallback, useEffect } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import {
  useProgramConsentQuery,
  useCreateConsentMutation,
  useUpdateConsentMutation,
  useDeleteConsentMutation,
} from '@common/cache';
import { formatApiError } from '@common/errors/error-formatting';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  consentFormSchema,
  ConsentFormValues,
  ConsentEntryValues,
  ConsentBody,
  ConsentResponse,
  ConsentType,
  ConsentHowObtained,
  ConsentStatus,
  ConsentObtainedBy,
  ConsentWhoConsented,
  ConsentExpires,
  getConsentTypeOptions,
} from '../schemas';

dayjs.extend(utc);

interface IUseConsentProps {
  program_id: string;
  patient_id: string;
  programName: string;
}

export const makeDefaultConsentEntry = (
  programName = '',
): ConsentEntryValues => {
  const options = getConsentTypeOptions(programName);
  return {
    id: undefined,
    consent_type: options.length === 1 ? options[0].value : '',
    how_obtained: '',
    date_obtained: '',
    status: '',
    obtained_by: '',
    who_consented: '',
    other_specify: '',
    expires: '',
  };
};

const mapResponseToEntry = (r: ConsentResponse): ConsentEntryValues => ({
  id: r.id,
  consent_type: r.consent_type,
  how_obtained: r.how_obtained,
  date_obtained: r.date_obtained
    ? dayjs.utc(r.date_obtained).format('YYYY-MM-DD')
    : '',
  status: r.status,
  obtained_by: r.obtained_by,
  who_consented: r.who_consented,
  other_specify: r.other_specify ?? '',
  expires: r.expires,
});

const toBody = (entry: ConsentEntryValues): ConsentBody => ({
  consent_type: entry.consent_type as ConsentType,
  how_obtained: entry.how_obtained as ConsentHowObtained,
  date_obtained: dayjs.utc(entry.date_obtained).toISOString(),
  status: entry.status as ConsentStatus,
  obtained_by: entry.obtained_by as ConsentObtainedBy,
  who_consented: entry.who_consented as ConsentWhoConsented,
  ...(entry.who_consented === 'Other' && {
    other_specify: entry.other_specify,
  }),
  expires: entry.expires as ConsentExpires,
});

// consent_type is derived from the program and pre-filled on a new draft (it is
// read-only in the UI), so it is deliberately ignored here: a fresh entry must
// stay "blank" — and therefore un-dirty — until the user fills something in.
const isBlankEntry = (entry: ConsentEntryValues): boolean =>
  !entry.how_obtained &&
  !entry.date_obtained &&
  !entry.status &&
  !entry.obtained_by &&
  !entry.who_consented &&
  !entry.other_specify &&
  !entry.expires;

// A consent is "complete" once every required field is filled (other_specify
// is only required when who_consented is 'Other').
const isEntryComplete = (entry: ConsentEntryValues): boolean =>
  !!entry.consent_type &&
  !!entry.how_obtained &&
  !!entry.date_obtained &&
  !!entry.status &&
  !!entry.obtained_by &&
  !!entry.who_consented &&
  !!entry.expires &&
  (entry.who_consented !== 'Other' || !!entry.other_specify);

export const useConsent = ({
  program_id,
  patient_id,
  programName,
}: IUseConsentProps) => {
  const { data: consents, isSuccess } = useProgramConsentQuery({
    program_id,
    patient_id,
  });

  const { mutateAsync: createConsent } = useCreateConsentMutation({
    program_id,
    patient_id,
  });
  const { mutateAsync: updateConsent } = useUpdateConsentMutation({
    program_id,
    patient_id,
  });
  const { mutateAsync: deleteConsent, isPending: isDeleting } =
    useDeleteConsentMutation({ program_id, patient_id });

  const formMethods = useForm<ConsentFormValues>({
    mode: 'onChange',
    resolver: yupResolver(consentFormSchema as never),
    defaultValues: { consents: [] },
  });

  const {
    control,
    formState: { isSubmitting },
    reset,
    trigger,
    getValues,
  } = formMethods;

  const fieldArray = useFieldArray({ control, name: 'consents' });
  const { fields, prepend, remove } = fieldArray;

  const resetForm = useCallback(() => {
    const existing = (consents ?? []).map(mapResponseToEntry);
    // Always keep at least one (blank) consent so the user has something to fill.
    reset({
      consents:
        existing.length > 0 ? existing : [makeDefaultConsentEntry(programName)],
    });
  }, [consents, reset, programName]);

  useEffect(() => {
    // Populate the form only once the consents have been fetched, so a single
    // existing consent is not masked by the initial placeholder entry.
    if (isSuccess) {
      resetForm();
    }
  }, [isSuccess, resetForm]);

  const isEntryDirty = useCallback(
    (entry: ConsentEntryValues): boolean => {
      if (!entry.id) {
        // A brand-new draft only counts as unsaved once the user has typed in it.
        return !isBlankEntry(entry);
      }
      const original = (consents ?? []).find(r => r.id === entry.id);
      if (!original) return true;
      // Compare against the mapped entry so both sides use the form's shape
      // (e.g. date_obtained as 'YYYY-MM-DD' rather than the raw ISO string).
      const orig = mapResponseToEntry(original);
      return (
        entry.consent_type !== orig.consent_type ||
        entry.how_obtained !== orig.how_obtained ||
        entry.date_obtained !== orig.date_obtained ||
        entry.status !== orig.status ||
        entry.obtained_by !== orig.obtained_by ||
        entry.who_consented !== orig.who_consented ||
        (entry.other_specify ?? '') !== orig.other_specify ||
        entry.expires !== orig.expires
      );
    },
    [consents],
  );

  const watchedConsents = useWatch({ control, name: 'consents' });
  // Per-entry dirty state, aligned with the `fields` indexes, so each accordion
  // can disable its own Save button while nothing has changed.
  const dirtyEntries = (watchedConsents ?? []).map(isEntryDirty);
  const hasUnsavedChanges = dirtyEntries.some(Boolean);

  // A new consent can only be added once the most recent one (top of the list)
  // is fully completed.
  const canAdd =
    (watchedConsents ?? []).length === 0 || isEntryComplete(watchedConsents[0]);

  const saveSingleEntry = useCallback(
    async (entry: ConsentEntryValues): Promise<boolean> => {
      const isNew = !entry.id;
      try {
        if (entry.id) {
          await updateConsent({ consent_id: entry.id, ...toBody(entry) });
        } else {
          await createConsent(toBody(entry));
        }
        toast.success(`Consent ${isNew ? 'created' : 'updated'} successfully`);
        return true;
      } catch (error) {
        toast.error(
          `Consent ${isNew ? 'create' : 'update'} failed: ${formatApiError(error)}`,
        );
        return false;
      }
    },
    [createConsent, updateConsent],
  );

  const submitEntry = useCallback(
    async (index: number): Promise<boolean> => {
      const valid = await trigger(`consents.${index}`);
      if (!valid) return false;
      const entry = getValues(`consents.${index}`);
      if (entry.id && !isEntryDirty(entry)) return true;
      const success = await saveSingleEntry(entry);
      if (success) {
        reset(getValues());
      }
      return success;
    },
    [trigger, getValues, isEntryDirty, saveSingleEntry, reset],
  );

  const submit = useCallback(async (): Promise<boolean> => {
    const values = getValues();
    const dirtyIndexes = values.consents
      .map((entry, index) => ({ entry, index }))
      .filter(({ entry }) => isEntryDirty(entry))
      .map(({ index }) => index);

    if (dirtyIndexes.length === 0) return true;

    const valid = await trigger(
      dirtyIndexes.map(i => `consents.${i}` as const),
    );
    if (!valid) return false;

    const results = await Promise.allSettled(
      dirtyIndexes.map(i => saveSingleEntry(values.consents[i])),
    );
    const hasError = results.some(
      r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value),
    );
    if (!hasError) {
      reset(getValues());
    }
    return !hasError;
  }, [getValues, isEntryDirty, trigger, saveSingleEntry, reset]);

  const handleAdd = useCallback(() => {
    // New draft accordion is added at the top of the list.
    prepend(makeDefaultConsentEntry(programName));
  }, [prepend, programName]);

  const deleteEntry = useCallback(
    async (index: number): Promise<void> => {
      const entry = getValues(`consents.${index}`);
      if (entry?.id) {
        try {
          await deleteConsent({ consent_id: entry.id });
          toast.success('Consent deleted successfully');
        } catch (error) {
          toast.error(`Delete failed: ${formatApiError(error)}`);
          throw error;
        }
        return;
      }
      // Unsaved draft: just drop it from the list.
      remove(index);
    },
    [getValues, deleteConsent, remove],
  );

  return {
    formMethods,
    fields,
    isDirty: hasUnsavedChanges,
    dirtyEntries,
    isSubmitting: isSubmitting || isDeleting,
    resetForm,
    submit,
    submitEntry,
    deleteEntry,
    handleAdd,
    canAdd,
    isEntryDirty,
  };
};
